import {Popover, PopoverContent, PopoverTrigger} from "@/components/ui/popover";
import {FormControl} from "@/components/ui/form";
import {Button} from "@/components/ui/button";
import {cn} from "@/lib/utils";
import {Check, ChevronsUpDown} from "lucide-react";
import {Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList} from "@/components/ui/command";
import {ComponentProps, ReactNode, useState} from "react";

export type ComboboxProps<O extends any, GetValueFn extends ((option: O) => any)> = {
    value?: ReturnType<GetValueFn>;
    onChange: (value: ReturnType<GetValueFn>) => void;
    options: O[];
    getValue: GetValueFn;
    getOptionLabel: (option: O) => ReactNode
    getSelectedLabel: (value: ReturnType<GetValueFn>, option?: O) => ReactNode
    placeholder?: ReactNode;
    searchText?: string;
    notFoundText?: ((options: O[]) => ReactNode) | ReactNode;
    inputProps?: ComponentProps<typeof CommandInput>
    commandProps?: ComponentProps<typeof Command>
    listProps?: ComponentProps<typeof CommandList>
    emptyProps?: ComponentProps<typeof CommandEmpty>
    groupProps?: ComponentProps<typeof CommandGroup>
    itemProps?: ComponentProps<typeof CommandItem> | ((option: O) => ComponentProps<typeof CommandItem>)
    checkProps?: ComponentProps<typeof Check>
} & Omit<ComponentProps<typeof Button>, 'onChange'>

export function Combobox<O extends any, GetValueFn extends ((option: O) => any)>({
                                                                                     value,
                                                                                     onChange,
                                                                                     searchText = "Search ...",
                                                                                     notFoundText = "No results found",
                                                                                     options,
                                                                                     placeholder,
                                                                                     getValue,
                                                                                     getOptionLabel,
                                                                                     getSelectedLabel,
                                                                                     inputProps,
                                                                                     commandProps,
                                                                                     listProps,
                                                                                     emptyProps,
                                                                                     groupProps,
                                                                                     itemProps,
                                                                                     checkProps,
                                                                                     ...props
                                                                                 }: ComboboxProps<O, GetValueFn>) {
    const [open, setOpen] = useState(false)

    return (
        <>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <FormControl>
                        <Button
                            onClick={() => setOpen(true)}
                            variant="outline"
                            role="combobox"
                            {...props}
                            className={cn(
                                "justify-between",
                                props?.className,
                            )}
                        >
                            <div className={cn("flex flex-row items-center justify-between w-full")}>
                                {value ? getSelectedLabel(value) : placeholder}
                                <ChevronsUpDown className="opacity-50"/>
                            </div>
                        </Button>
                    </FormControl>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0">
                    <Command  {...commandProps}>
                        <CommandInput
                            placeholder={searchText}
                            className="h-9"
                            {...inputProps}
                        />
                        <CommandList {...listProps}>
                            <CommandEmpty {...emptyProps}>{typeof notFoundText ==='function' ? notFoundText(options) : notFoundText}</CommandEmpty>
                            <CommandGroup {...groupProps}>
                                {options.map((option, i) => {
                                    const optionValue = getValue(option)
                                    const optionLabel = getOptionLabel(option)
                                    return <CommandItem
                                        key={i + optionValue?.toString() + optionLabel?.toString()}
                                        onSelect={() => {
                                            onChange(optionValue)
                                            setOpen(false)
                                        }}
                                        {...(typeof itemProps === 'function' ? itemProps(option) : itemProps)}
                                    >
                                        {optionLabel}
                                        <Check
                                            {...checkProps}

                                            className={cn(
                                                "ml-auto",
                                                optionValue === value
                                                    ? "opacity-100"
                                                    : "opacity-0",
                                                checkProps?.className
                                            )}
                                        />
                                    </CommandItem>;
                                })}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
        </>
    )
}

Combobox.displayName = "Combobox";
